import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import invariant from "tiny-invariant";
import { Grid, Typography, useTheme } from "@mui/material";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { toast } from "react-toastify";
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { reorder } from "@atlaskit/pragmatic-drag-and-drop/reorder";
import { triggerPostMoveFlash } from "@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash";
import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { getReorderDestinationIndex } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index";
import * as liveRegion from "@atlaskit/pragmatic-drag-and-drop-live-region";
import {
  getItemRegistry,
  isItemData,
  getItemPosition,
} from "../../../utils/dndUtils";
import GroupPickListItem from "./GroupPickListItem";

const ListContext = createContext(null);

export const useListContext = () => {
  const listContext = useContext(ListContext);
  invariant(listContext !== null);
  return listContext;
};

const GroupPickCard = (props) => {
  const {
    group,
    updateGroupPicks,
    commitGroup,
    setCardActive,
    currGroup,
    setCurrentGroup,
  } = props;
  const theme = useTheme();

  const [isFocused, setIsFocused] = useState(false);
  const committingRef = useRef(false);
  const [{ items, lastCardMoved }, setListState] = useState({
    items: _.orderBy(group.teams, ["groupPosition"], ["asc"]),
    lastCardMoved: null,
  });
  const [registry] = useState(getItemRegistry);

  // Isolated instances of this component from one another
  const [instanceId] = useState(() => Symbol("instance-id"));

  const reorderItem = useCallback(
    ({ startIndex, indexOfTarget, closestEdgeOfTarget }) => {
      const finishIndex = getReorderDestinationIndex({
        startIndex,
        closestEdgeOfTarget,
        indexOfTarget,
        axis: "vertical",
      });

      if (finishIndex === startIndex) {
        // If there would be no change, we skip the update
        return;
      }

      setListState((listState) => {
        const item = listState.items[startIndex];

        return {
          items: reorder({
            list: listState.items,
            startIndex,
            finishIndex,
          }),
          lastCardMoved: {
            item,
            previousIndex: startIndex,
            currentIndex: finishIndex,
            numberOfItems: listState.items.length,
          },
        };
      });

      if (!group?.cardActive) {
        setCardActive(true, group?.group);
      }
    },
    [setCardActive, group],
  );

  useEffect(() => {
    return monitorForElements({
      canMonitor({ source }) {
        return isItemData(source.data) && source.data.instanceId === instanceId;
      },
      onDrop({ location, source }) {
        const target = location.current.dropTargets[0];
        if (!target) {
          return;
        }

        const sourceData = source.data;
        const targetData = target.data;
        if (!isItemData(sourceData) || !isItemData(targetData)) {
          return;
        }

        const indexOfTarget = items.findIndex(
          (item) => item.id === targetData.item.id,
        );
        if (indexOfTarget < 0) {
          return;
        }

        const closestEdgeOfTarget = extractClosestEdge(targetData);

        reorderItem({
          startIndex: sourceData.index,
          indexOfTarget,
          closestEdgeOfTarget,
        });
      },
    });
  }, [instanceId, items, reorderItem]);

  // once a drag is finished, some post drop actions
  useEffect(() => {
    if (lastCardMoved === null) {
      return;
    }

    const { item, previousIndex, currentIndex, numberOfItems } = lastCardMoved;
    const element = registry.getElement(item.id);
    if (element) {
      triggerPostMoveFlash(element);
    }

    liveRegion.announce(
      `You've moved ${item?.label} from position ${
        previousIndex + 1
      } to position ${currentIndex + 1} of ${numberOfItems}.`,
    );
  }, [lastCardMoved, registry]);

  // cleanup the live region when this component is finished
  useEffect(() => {
    return function cleanup() {
      liveRegion.cleanup();
    };
  }, []);

  useEffect(() => {
    const focused = currGroup === group?.group;
    setIsFocused(focused);
  }, [currGroup, group?.group]);

  // Auto-save this group's visible order when the user moves away from the
  // card — clicking/tabbing to another group or anywhere outside it. Replaces
  // the old "Accept these picks" button: clicking the card makes it active
  // (onClick -> setCurrentGroup) and focus leaving it commits the order.
  // No-op once the group is already registered (a drag persists it immediately
  // and flips cardActive) or while a commit is already in flight. The
  // contains(relatedTarget) guard ignores focus moving between the card's own
  // children, so we only fire on a genuine "leave".
  const handleCardBlur = useCallback(
    async (e) => {
      if (e.currentTarget.contains(e.relatedTarget)) return;
      if (!commitGroup || group?.cardActive || committingRef.current) return;
      committingRef.current = true;
      const ok = await commitGroup(group);
      committingRef.current = false;
      if (!ok) {
        toast(`Couldn't save Group ${group?.group} — please try again`, {
          type: "error",
          position: "bottom-right",
          autoClose: 2500,
          hideProgressBar: true,
          closeOnClick: false,
        });
      }
    },
    [commitGroup, group],
  );

  const getListLength = useCallback(() => items?.length, [items?.length]);

  const contextValue = useMemo(() => {
    return {
      registerItem: registry.register,
      reorderItem,
      instanceId,
      getListLength,
    };
  }, [registry.register, reorderItem, instanceId, getListLength]);

  return (
    <ListContext.Provider value={contextValue}>
      <Grid
        key={`group-picks-card-${group?.group}`}
        id={group?.nodeId}
        container
        direction="column"
        tabIndex={0}
        onClick={() => {
          setCurrentGroup(group?.group);
        }}
        onBlur={handleCardBlur}
        sx={[
          {
            width: "100%",
            position: "relative",
            padding: "26px",
            gap: "16px",
            backgroundColor: theme.branding.gray[0],
            borderRadius: "14px",
            border: `2px ${group?.cardActive ? "solid" : "dashed"} ${isFocused ? theme.branding.cyan[200] : theme.branding.gray[200]}`,
            boxShadow: isFocused
              ? `0px 0px 15px -3px ${theme.branding.cyan[200]}`
              : group?.cardActive
                ? `0px 3px 7px -3px ${theme.branding.gray[200]}`
                : "none",
            transition: "all 0.5s ease-out",
            opacity: group?.cardActive || isFocused ? "100%" : "60%",
            "&:focus": {
              boxShadow: `0px 0px 15px -3px ${theme.branding.cyan[200]}`,
              borderColor: theme.branding.cyan[200],
            },
          },
          (theme) =>
            theme.applyStyles("dark", {
              backgroundColor: theme.branding.darkBlue[800],
              borderColor:
                currGroup === group?.group
                  ? theme.branding.cyan[300]
                  : theme.branding.darkBlue[600],
              "&:focus": {
                borderColor: theme.branding.cyan,
              },
            }),
        ]}
      >
        <Grid container direction="column" sx={{ gap: "4px" }}>
          <Typography variant="h4">Group {group?.group}</Typography>
          <Typography
            variant="caption"
            sx={[
              (theme) =>
                theme.applyStyles("dark", {
                  color: theme.branding.gray[400],
                }),
            ]}
          >
            Drag to reorder
          </Typography>
        </Grid>

        {/* team list items */}
        <Grid container direction="column" sx={{ gap: "8px" }}>
          {items?.map((item, index) => (
            <GroupPickListItem
              key={`gp-li-${item?.id}-${index}`}
              item={item}
              index={index}
              position={getItemPosition({ index, items })}
              updateGroupPicks={updateGroupPicks}
              group={group}
              setCurrentGroup={setCurrentGroup}
            />
          ))}
        </Grid>

        {/* divider and auto-qual info */}
        <Grid container direction="column" sx={{ gap: "16px" }}>
          <Grid
            sx={[
              {
                width: "100%",
                height: "1px",
                backgroundColor: theme.branding.gray[100],
              },
              (theme) =>
                theme.applyStyles("dark", {
                  borderColor: theme.branding.darkBlue[600],
                }),
            ]}
          />
          <Grid container direction="row" sx={{ gap: "12px" }}>
            <Grid
              container
              direction="row"
              wrap="nowrap"
              sx={{
                gap: "6px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Grid
                sx={[
                  {
                    height: "12px",
                    width: "12px",
                    borderRadius: "4px",
                    backgroundColor: theme.branding.green[500],
                    color: theme.branding.gray[500],
                  },
                  (theme) =>
                    theme.applyStyles("dark", {
                      color: theme.branding.gray[400],
                    }),
                ]}
              />
              <Typography variant="caption" sx={{ color: "inherit" }}>
                Auto-qualify
              </Typography>
            </Grid>
            <Grid
              container
              direction="row"
              wrap="nowrap"
              sx={{
                gap: "6px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Grid
                sx={{
                  height: "12px",
                  width: "12px",
                  borderRadius: "4px",
                  backgroundColor: theme.branding.yellow[500],
                }}
              />
              <Typography variant="caption" sx={{ color: "inherit" }}>
                Bubble
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        {/* Save status. A group registers automatically the moment you drag to
            reorder it, or when you move focus away from the card (handled by
            onBlur above) — no explicit "accept" action needed. */}
        <Grid container>
          {group?.cardActive ? (
            <Grid
              container
              direction="row"
              wrap="nowrap"
              sx={{
                gap: "6px",
                alignItems: "center",
                justifyContent: "center",
                color: theme.branding.teal,
              }}
            >
              <CheckOutlinedIcon
                sx={{ height: "16px", width: "16px", color: "inherit" }}
              />
              <Typography
                variant="caption"
                sx={{ color: "inherit", fontWeight: 600 }}
              >
                Picks accepted
              </Typography>
            </Grid>
          ) : (
            <Grid
              container
              sx={{ justifyContent: "center", alignItems: "center" }}
            >
              <Typography
                variant="caption"
                sx={[
                  { color: theme.branding.gray[400], fontStyle: "italic" },
                  (theme) =>
                    theme.applyStyles("dark", {
                      color: theme.branding.gray[500],
                    }),
                ]}
              >
                Saved when you move on
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
    </ListContext.Provider>
  );
};

export default GroupPickCard;
